import { Api } from './axios-config';
import { IBusinessView, IDayScheduleView } from './businessService';
import { IWeeklyScheduleDay } from './businessHolidayService';
import { IServiceView } from './serviceService';
import { IUserView } from './userService';

export interface IEmployeeView {
  id: number;
  userId: number;
  user: IUserView;
  businessEmployees: IBusinessEmployeeView[];
}

export interface IBusinessEmployeeView {
  id: number;
  name: string;
  profilePicture?: string;
  servicesCommissionRate: number;
  color: string;
  isActive: boolean;
  isReceptionist: boolean;
  businessId: number;
  business: IBusinessView;
  employeeId?: number;
  employee?: IEmployeeView;
  businessEmployeeServices?: IBusinessEmployeeServiceView[];
  weeklySchedules?: IWeeklyScheduleDay[];
}

export interface IBusinessEmployeeServiceView {
  id: number;
  businessEmployeeId: number;
  serviceId: number;
  serviceDuration: number;
  service: IServiceView;
}

const getById = async (id: number): Promise<IEmployeeView> => {
  try {
    const { data } = await Api.get<IEmployeeView>(
      `/employee/${id}`
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const getByUserId = async (userId: number): Promise<IEmployeeView> => {
  try {
    const { data } = await Api.get<IEmployeeView>(
      `/employee/user/${userId}`
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const get = async (
  businessId?: number,
  serviceId?: number
): Promise<IEmployeeView[]> => {
  try {
    const queryParams = {
      businessId: businessId?.toString(),
      serviceId: serviceId?.toString()
    };

    const { data } = await Api.get('/employee', {
      params: queryParams,
    });

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface ICreateEmployee {
  userId: number;
  businessEmployee: ICreateBusinessEmployee;
}

export interface ICreateBusinessEmployee {
  businessId: number;
  name?: string;
  profilePicture?: string;
  servicesCommissionRate: number;
  color: string;
  isActive?: boolean;
  isReceptionist?: boolean;
  businessEmployeeServices?: IBusinessEmployeeService[];
  weeklySchedules: { dayOfWeek: number; isClosed: boolean; startTime?: string | null; endTime?: string | null }[];
}

const create = async (body: ICreateEmployee): Promise<number> => {
  try {
    const { data } = await Api.post(
      '/employee',
      body
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface IUpdateEmployee {
  id: number;
  userId: number;
  businessEmployees: IUpdateBusinessEmployee[];
}

export interface IUpdateBusinessEmployee {
  id: number;
  name?: string;
  profilePicture?: string;
  employeeId?: number;
  businessId: number;
  servicesCommissionRate: number;
  color: string;
  isActive?: boolean;
  isReceptionist?: boolean;
  businessEmployeeServices?: IBusinessEmployeeService[];
  weeklySchedules?: { dayOfWeek: number; isClosed: boolean; startTime?: string | null; endTime?: string | null }[];
}

export interface IBusinessEmployeeService {
  businessEmployeeId?: number;
  serviceId: number;
  serviceDuration: number;
}

const update = async (body: IUpdateEmployee): Promise<IEmployeeView> => {
  try {
    const { data } = await Api.put(
      '/employee',
      body
    );

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const remove = async (id: number): Promise<void> => {
  try {
    await Api.delete(
      `/employee/${id}`
    );

  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface IAcceptInvite {
  token: string;
  weeklySchedules: { dayOfWeek: number; isClosed: boolean; startTime?: string | null; endTime?: string | null }[];
  color: string;
  servicesCommissionRate?: number;
  isReceptionist?: boolean;
}

export interface IInviteInfo {
  email: string;
  businessId: number;
  businessName: string;
  isReceptionist: boolean;
  weeklySchedules?: IDayScheduleView[];
}

const getInviteInfo = async (token: string): Promise<IInviteInfo> => {
  try {
    const { data } = await Api.get<IInviteInfo>('/employee/invite-info', {
      params: { token }
    });

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const acceptInvite = async (body: IAcceptInvite): Promise<IUserView> => {
  try {
    const { data } = await Api.post<IUserView>('/employee/accept-invite', body);

    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export const employeeService = {
  getById,
  getByUserId,
  get,
  create,
  update,
  remove,
  getInviteInfo,
  acceptInvite
};